import { state, api, $, $$, toast, escHtml, truncate, todayStr, _timeAgo, _ensureMachineCache, _allMachinesCache, _lineMap, _lineLabels, _buildLineMap, _updateMachineSelect, _populateLineSelect, _linkLineToMachineSelect, _insertLineSelectBefore, setAllMachinesCache, _updateHelpBadge, customConfirm, formatDateRO, formatDateTimeRO, _eLinieProductie, _eticheta_linie } from './core';
import {
    workEnrichmentsPanelHtml, workWireEnrichmentForm, workRefreshEnrichmentsPanel,
    workPause, workResume, workFetchActiveUrgentLogs, WORK_LIFECYCLE_LABELS,
} from './work-session';
import { colleagueLoadBrief, colleagueWireChat, colleagueOfferLesson, colleagueMd } from './colleague';
import { askText } from './dialogs';
import { confirmaScrierea, confirmaUnText } from './corectura';
import type { JurnalDetaliu, MediaLucrare } from './types';
import type { RandMaintenanceLog } from './tipuri-baza';

/** un pas al intervenției ghidate de AI, salvat în `notes` (JSON `{type:'ai_guided', steps:[…]}`) */
interface PasSalvat { step: number; text: string; completed: boolean; feedback: string; deleted: boolean; }
/** /api/maintenance_log/similar (routes/maintenance/jurnal.py): câteva coloane + potrivirea cu problema */
interface LucrareSimilara extends Pick<RandMaintenanceLog, 'id' | 'machine_id' | 'date' | 'action' | 'technician' | 'notes'
        | 'recording_filename' | 'duration_minutes' | 'validation_status' | 'parts_used' | 'cause_found'> {
    relevance: number;
}
/** /api/maintenance_log/<id>/transcrieri (routes/maintenance/media.py): filmările lucrării cu transcrierea lor */
interface TranscriereFilmare {
    media_id: number; filename: string; duration_s: number | null; arhivat: number | null;
    transcript: string | null; status: string | null; processed_at: string | null;
}
/** /api/documents/relevant_chapters (routes/documents/documente.py): capitol sau, la rezervă, pagină (`chapter_id` null) */
interface CapitolRelevant {
    chapter_id: number | null; document_id: number; filename: string; chapter_number: string; title: string;
    start_page: number | null; end_page: number | null; relevance: number; pdf_url: string;
}
/** câmpurile ciornei intervenției: cheia e coloana din maintenance_log, sau `constatari`/`actiuni` din ciornă */
type CampIntv = 'involved_persons' | 'constatari' | 'actiuni' | 'cause_found' | 'parts_used' | 'notes';

/* ═══ JURNAL MENTENANȚĂ ════════════════════════════════════════ */
export let journalInited = false;

export async function loadJournal() {
    _renderSupervizareUrgente();   // coada inginerului — la fiecare deschidere a secțiunii
    if (!journalInited) {
        const machines = await api('/api/machines/all');
        setAllMachinesCache(machines);

        const lineSel = $('#jrn-line');
        if (lineSel) {
            lineSel.innerHTML = '<option value="">— Selectează linia —</option>';
            const prodLines = Object.keys(_lineMap).filter(k => _eLinieProductie(k)).sort();
            const utilLines = Object.keys(_lineMap).filter(k => !_eLinieProductie(k) && k !== '_other');
            const hasOther = !!_lineMap['_other'];

            if (prodLines.length) {
                lineSel.innerHTML += '<optgroup label="Linii producție">';
                prodLines.forEach(k => {
                    const label = _eticheta_linie(k);
                    const product = _lineMap[k][0]?.product || '';
                    const suffix = product ? ` — ${product}` : '';
                    lineSel.innerHTML += `<option value="${k}">${label}${suffix} (${_lineMap[k].length} utilaje)</option>`;
                });
                lineSel.innerHTML += '</optgroup>';
            }
            if (_lineMap['_racitoare']?.length) {
                lineSel.innerHTML += '<optgroup label="Răcitoare externe">';
                lineSel.innerHTML += `<option value="_racitoare">${_lineLabels['_racitoare']} (${_lineMap['_racitoare'].length} utilaje)</option>`;
                lineSel.innerHTML += '</optgroup>';
            }
            if (utilLines.length) {
                lineSel.innerHTML += '<optgroup label="Utilități & Accesorii">';
                utilLines.forEach(k => {
                    const label = _eticheta_linie(k);
                    lineSel.innerHTML += `<option value="${k}">${label} (${_lineMap[k].length} utilaje)</option>`;
                });
                lineSel.innerHTML += '</optgroup>';
            }
            if (hasOther) {
                lineSel.innerHTML += `<option value="_other">Alte utilaje nealocate (${_lineMap['_other'].length})</option>`;
            }

            lineSel.addEventListener('change', () => _updateMachineSelect((lineSel as HTMLInputElement).value));
        }

        const filterLineSel = $('#jrn-filter-line');
        if (filterLineSel) {
            filterLineSel.innerHTML = '<option value="">Toate liniile</option>';
            const fProd = Object.keys(_lineMap).filter(k => _eLinieProductie(k)).sort();
            const fUtil = Object.keys(_lineMap).filter(k => !_eLinieProductie(k) && k !== '_other' && k !== '_racitoare');
            if (fProd.length) {
                filterLineSel.innerHTML += '<optgroup label="Linii producție">';
                fProd.forEach(k => { filterLineSel.innerHTML += `<option value="${k}">${_eticheta_linie(k)}</option>`; });
                filterLineSel.innerHTML += '</optgroup>';
            }
            if (_lineMap['_racitoare']?.length) {
                filterLineSel.innerHTML += '<optgroup label="Răcitoare externe">';
                filterLineSel.innerHTML += `<option value="_racitoare">${_lineLabels['_racitoare']}</option>`;
                filterLineSel.innerHTML += '</optgroup>';
            }
            if (fUtil.length) {
                filterLineSel.innerHTML += '<optgroup label="Utilități & Accesorii">';
                fUtil.forEach(k => { filterLineSel.innerHTML += `<option value="${k}">${_eticheta_linie(k)}</option>`; });
                filterLineSel.innerHTML += '</optgroup>';
            }
            if (_lineMap['_other']) {
                filterLineSel.innerHTML += `<option value="_other">Alte utilaje nealocate</option>`;
            }
            filterLineSel.addEventListener('change', () => {
                const lk = (filterLineSel as HTMLInputElement).value;
                const fmSel = $('#jrn-filter-machine');
                if (!fmSel) return;
                if (!lk) {
                    fmSel.innerHTML = '<option value="">Toate utilajele</option>';
                    _allMachinesCache!.forEach(m => { fmSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
                } else {
                    const lineMachines = _lineMap[lk] || [];
                    fmSel.innerHTML = '<option value="">Toate utilajele din linie</option>';
                    lineMachines.forEach(m => { fmSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
                }
            });
        }

        const filterSel = $('#jrn-filter-machine');
        filterSel!.innerHTML = '<option value="">Toate utilajele</option>';
        _allMachinesCache!.forEach(m => { filterSel!.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });

        if (state.currentUser && state.currentUser.username) {
            const opField = $('#jrn-operator');
            if (opField && !(opField as HTMLInputElement).value) {
                (opField as HTMLInputElement).value = state.currentUser.display_name || state.currentUser.username;
            }
        }

        ($('#jrn-date') as HTMLInputElement).value = todayStr();
        const jrnOp = $('#jrn-operator');
        if (jrnOp && !(jrnOp as HTMLInputElement).value && state.currentUser?.username) {
            (jrnOp as HTMLInputElement).value = state.currentUser.username;
        }
        journalInited = true;
    }

    urgShowPhase('urg-phase-1');
    loadJournalLog();
    _renderUrgentReopenList();

    $('#jrn-refresh')!.onclick = loadJournalLog;
    document.getElementById('jrn-reset')?.addEventListener('click', () => {
        if ($('#jrn-filter-line')) {
            ($('#jrn-filter-line') as HTMLInputElement).value = '';
            const fmSel = $('#jrn-filter-machine');
            if (fmSel) {
                fmSel.innerHTML = '<option value="">Toate utilajele</option>';
                _allMachinesCache!.forEach(m => { fmSel.innerHTML += `<option value="${m.id}">${m.id} — ${escHtml(m.name || '')}</option>`; });
            }
        }
        if ($('#jrn-filter-machine')) ($('#jrn-filter-machine') as HTMLInputElement).value = '';
        if ($('#jrn-filter-from')) ($('#jrn-filter-from') as HTMLInputElement).value = '';
        if ($('#jrn-filter-to')) ($('#jrn-filter-to') as HTMLInputElement).value = '';
        loadJournalLog();
    });
}

export function _getMachineLineId(machineId: string | null | undefined) {
    if (!_allMachinesCache) return '';
    const m = _allMachinesCache.find(x => x.id === machineId);
    return m ? (m.line || '') : '';
}

export async function loadJournalLog() {
    const lineFilter = ($('#jrn-filter-line') as HTMLInputElement)?.value;
    const machine = ($('#jrn-filter-machine') as HTMLInputElement)?.value;
    const from = ($('#jrn-filter-from') as HTMLInputElement)?.value;
    const to = ($('#jrn-filter-to') as HTMLInputElement)?.value;
    const p = new URLSearchParams();
    if (machine) {
        p.set('machine_id', machine);
    } else if (lineFilter) {
        const lineMachines = _lineMap[lineFilter] || [];
        if (lineMachines.length) p.set('machine_ids', lineMachines.map(m => m.id).join(','));
    }
    if (from) p.set('from', from);
    if (to) p.set('to', to);
    p.set('category', 'urgent');
    const logs: RandMaintenanceLog[] = await api(`/api/maintenance_log?${p}`);

    const tbody = $('#journal-table tbody');
    tbody!.innerHTML = '';

    if (logs.length === 0) {
        tbody!.innerHTML = '<tr><td colspan="9" class="empty-state"><div class="icon">📋</div>Nicio înregistrare în jurnal</td></tr>';
    } else {
        logs.forEach((l) => {
            const vs = l.validation_status || 'pending';
            const vsMap: Record<string, { label: string; bg: string; color: string }> = {
                pending:               { label: 'Așteaptă',      bg: '#f59e0b', color: '#000' },
                validated:             { label: 'Validat',        bg: '#22c55e', color: '#fff' },
                rejected:              { label: 'Respins',        bg: '#ef4444', color: '#fff' },
                needs_clarification:   { label: 'Clarificare!',   bg: '#f97316', color: '#000' }
            };
            const vsCfg = vsMap[vs] || { label: vs, bg: 'gray', color: '#fff' };
            const vsBadge = `<span style="display:inline-block;padding:3px 10px;border-radius:12px;font-size:.78rem;font-weight:700;background:${vsCfg.bg};color:${vsCfg.color};white-space:nowrap">${escHtml(vsCfg.label)}</span>`;
            const tr = document.createElement('tr');
            tr.style.cursor = 'pointer';
            if (vs === 'needs_clarification') tr.style.background = 'rgba(249,115,22,.08)';
            const lineOfMachine = _getMachineLineId(l.machine_id);
            const lineLbl = lineOfMachine ? (_lineLabels[lineOfMachine] || lineOfMachine) : (l.line_id || '—');
            const regNr = l.registration_number || '—';
            tr.innerHTML = `
                <td style="font-size:.8rem;font-weight:600;color:var(--accent);white-space:nowrap">${escHtml(regNr)}</td>
                <td>${escHtml(formatDateRO(l.date))}</td>
                <td style="font-size:.85rem">${escHtml(lineLbl)}</td>
                <td><strong>${escHtml(l.machine_id)}</strong></td>
                <td style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${escHtml(l.action)}</td>
                <td>${escHtml(l.technician)}</td>
                <td>${vsBadge}</td>
                <td>${l.hours_at_service || '—'}</td>
                <td><button class="btn btn-sm jrn-view-btn" data-id="${l.id}" style="font-size:.85rem">👁 Deschide</button></td>
            `;
            tr.addEventListener('click', (e) => {
                if (!(e.target as HTMLElement).closest('.jrn-view-btn')) openLogDetail(l.id);
            });
            tbody!.appendChild(tr);
        });
        tbody!.querySelectorAll('.jrn-view-btn').forEach(btn => {
            btn.addEventListener('click', () => openLogDetail((btn as HTMLElement).dataset.id!));
        });
    }
}

export function urgShowPhase(id: string) {
    document.querySelectorAll('.urg-phase').forEach(p => (p as HTMLElement).style.display = 'none');
    const el = document.getElementById(id);
    if (el) el.style.display = 'block';
    if (id === 'urg-phase-1') _renderUrgentReopenList();
}

function _mountUrgentEnrichmentsPanel(): void {
    const mount = document.getElementById('intv-enrichments-mount');
    if (!mount || mount.dataset.mounted === '1') return;
    mount.innerHTML = workEnrichmentsPanelHtml('intv');
    mount.dataset.mounted = '1';
    workWireEnrichmentForm('maintenance_log', () => _intvLogId, 'intv');
}

/** Coada de supervizare a inginerului: urgențele deschise ACUM pe toată fabrica
 *  (audit UX 31 iul 2026). Operatorii nu o văd — ei au lista lor de reluare. */
export async function _renderSupervizareUrgente(): Promise<void> {
    const role = state.currentUser?.role || 'operator';
    if (role !== 'engineer' && role !== 'master') return;
    const sec = document.getElementById('sec-journal');
    if (!sec) return;
    let bloc = document.getElementById('urg-supervizare');
    if (!bloc) {
        bloc = document.createElement('div');
        bloc.id = 'urg-supervizare';
        bloc.style.cssText = 'margin:0 0 14px;padding:12px 14px;background:var(--surface);border:1px solid var(--border);border-radius:12px';
        sec.insertBefore(bloc, sec.firstElementChild);
    }
    try {
        const rows = await workFetchActiveUrgentLogs();
        if (!rows.length) { bloc.style.display = 'none'; return; }
        bloc.style.display = '';
        const acum = Date.now();
        let html = `<div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
            <strong>🚨 Urgențe deschise pe fabrică (${rows.length})</strong>
            <span style="color:var(--text-muted);font-size:.8rem">— supervizare inginer</span></div>`;
        rows.forEach((r) => {
            const start = Date.parse(r.start_time || r.date || '') || acum;
            const zile = Math.floor((acum - start) / 86400000);
            const vechime = zile >= 1 ? `${zile}z` : `${Math.max(1, Math.round((acum - start) / 3600000))}h`;
            const veche = zile >= 3;
            const stare = r.status === 'paused' ? '⏸ pauză' : '▶ în lucru';
            html += `<div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:6px 0;border-top:1px solid var(--border);font-size:.86rem">
                <span style="color:var(--cyan);font-weight:600;min-width:90px">${escHtml(r.machine_id || '')}</span>
                <span style="flex:1;min-width:150px">${escHtml(truncate(r.action || '', 70))}</span>
                <span style="color:var(--text-muted)">${escHtml(r.technician || '—')}</span>
                <span style="color:${veche ? '#ef5350' : 'var(--text-muted)'}" title="de când e deschisă">${veche ? '⚠ ' : ''}${vechime}</span>
                <span>${stare}</span>
                ${r.live ? '<span style="color:#ef5350;font-weight:700">🔴 LIVE</span>' : ''}
                <button type="button" class="btn btn-xs btn-outline urg-sup-open" data-log-id="${r.id}" >👁 Deschide</button>
                <button type="button" class="btn btn-xs urg-sup-close" data-log-id="${r.id}"
                        style="background:var(--green,#22c55e);color:#fff;border:none">✓ Finalizează</button>
            </div>`;
        });
        bloc.innerHTML = html;
        bloc.querySelectorAll('.urg-sup-open').forEach(btn => {
            btn.addEventListener('click', () => {
                const w = window as Window & { openLogDetail?: typeof openLogDetail };
                if (typeof w.openLogDetail === 'function') w.openLogDetail(parseInt((btn as HTMLElement).dataset.logId || '0', 10));
            });
        });
        /* Finalizarea din lista de supervizare (cerut de Dan, 31 aug 2026).
         *
         * Până acum aici era un singur buton, „Deschide", care duce la o fișă cu
         * VALIDEAZĂ / RESPINGE — iar validarea nu închidea lucrarea. Inginerul
         * vedea 22 de urgențe deschise, unele de 176 de zile, și n-avea de unde
         * să le închidă. Cele mai vechi nici nu erau intervenții, ci SESIZĂRI:
         * „nu se retrage aruncătorul", fără nicio linie despre ce s-a făcut.
         *
         * De-aia cerem un text OBLIGATORIU și îl scriem în dreptul lucrării ca
         * «Acțiuni: …» — aceeași convenție pe care o folosesc cele închise
         * normal. O intervenție închisă fără nicio vorbă e exact ce a produs
         * grămada asta. */
        bloc.querySelectorAll('.urg-sup-close').forEach(btn => {
            btn.addEventListener('click', async () => {
                const el = btn as HTMLButtonElement;
                const id = parseInt(el.dataset.logId || '0', 10);
                const r = rows.find((x) => x.id === id);
                if (!r) return;
                const text = await askText('Ce s-a făcut?', {
                    multiline: true, required: true,
                    placeholder: 'Ce s-a rezolvat, sau de ce se închide fără intervenție',
                    okText: 'Finalizează',
                });
                if (!text) return;
                el.disabled = true;
                el.textContent = '⏳…';
                try {
                    const actiune = (r.action || '').trim();
                    // verdictul îl pune serverul: închisă din cont de inginer/master = validată pe numele lui
                    const rez = await api('/api/maintenance_log/' + id, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            status: 'completed',
                            completed_time: new Date().toISOString(),
                            action: actiune + (actiune ? '\n\n' : '') + 'Acțiuni: ' + text.trim(),
                        }),
                    });
                    toast(rez && rez.validat_de ? 'Intervenția a fost finalizată și validată pe numele tău'
                        : 'Intervenția a fost finalizată — intră la validare', 'success');
                    await _renderSupervizareUrgente();
                } catch (e) {
                    el.disabled = false;
                    el.textContent = '✓ Finalizează';
                    toast((e as Error)?.message || 'Nu s-a putut finaliza', 'error');
                }
            });
        });
    } catch (_e) { bloc.style.display = 'none'; }
}

async function _renderUrgentReopenList(): Promise<void> {
    const section = document.getElementById('intv-reopen-section');
    const list = document.getElementById('intv-reopen-list');
    if (!section || !list) return;
    try {
        const rows = await workFetchActiveUrgentLogs();
        if (!rows.length) {
            section.style.display = 'none';
            return;
        }
        section.style.display = 'block';
        const acumMs = Date.now();
        list.innerHTML = rows.map(r => {
            const isPaused = r.status === 'paused';
            const stLabel = isPaused ? '⏸ În pauză' : '▶ Activă';
            const stClass = isPaused ? 'urg-reopen-status urg-reopen-status--paused' : 'urg-reopen-status urg-reopen-status--active';
            // Audit 2.14 — lista de redeschidere nu arăta NICIO dată: o intervenție
            // în pauză din 12 iunie arăta la fel cu una de ieri. Vechimea se ia din
            // momentul punerii în pauză, apoi din start, apoi din ziua lucrării.
            const refMs = Date.parse(r.paused_at || r.start_time || r.date || '') || 0;
            let vechimeHtml = '';
            if (refMs) {
                const zile = Math.floor((acumMs - refMs) / 86400000);
                const text = zile >= 1
                    ? `${zile} ${zile === 1 ? 'zi' : 'zile'}`
                    : `${Math.max(1, Math.round((acumMs - refMs) / 3600000))} h`;
                const dataRo = new Date(refMs).toLocaleDateString('ro-RO',
                    { day: 'numeric', month: 'long', year: 'numeric' });
                const veche = zile >= 3;
                vechimeHtml = `<span class="urg-reopen-age" style="color:${veche ? '#ef5350' : 'var(--text-muted)'};font-size:.8rem;margin-left:8px"
                    title="Deschisă/pusă în pauză la ${escHtml(dataRo)}">${veche ? '⚠ ' : ''}din ${escHtml(dataRo)} · ${text}</span>`;
            }
            return `<div class="urg-reopen-card">
                <div>
                    <div class="urg-reopen-card-title">${escHtml(r.registration_number || ('#' + r.id))} — ${escHtml(r.machine_id || '')}</div>
                    <div class="urg-reopen-card-desc">${escHtml((r.action || '').substring(0, 80))}</div>
                    <span class="${stClass}">${stLabel}</span>${vechimeHtml}
                </div>
                <button type="button" class="urg-op-btn urg-op-btn--green intv-reopen-btn" data-log-id="${r.id}">▶ ${WORK_LIFECYCLE_LABELS.resumeUrgent}</button>
            </div>`;
        }).join('');
        list.querySelectorAll('.intv-reopen-btn').forEach(btn => {
            btn.addEventListener('click', () => _reopenUrgentIntervention(parseInt((btn as HTMLElement).dataset.logId || '0', 10)));
        });
    } catch (_e) {
        section.style.display = 'none';
    }
}

async function _reopenUrgentIntervention(logId: number): Promise<void> {
    if (!logId) return;
    try {
        await workResume('maintenance_log', logId);
        const log: JurnalDetaliu = await api('/api/maintenance_log/' + logId);
        _intvLogId = log.id;
        _intvRegNumber = log.registration_number || '';
        (document.getElementById('intv-log-id')! as HTMLInputElement).value = String(log.id);
        const regEl = document.getElementById('intv-reg-number');
        if (regEl) regEl.textContent = _intvRegNumber;
        document.getElementById('intv-machine-label')!.textContent = log.machine_id || '';
        document.getElementById('intv-problem-label')!.textContent = (log.action || '').split('\n')[0];
        document.getElementById('intv-rpt-machine')!.textContent = log.machine_id || '';
        document.getElementById('intv-rpt-problem')!.textContent = (log.action || '').split('\n')[0];
        document.getElementById('intv-rpt-reporter')!.textContent = log.technician || '';
        // 30 sep 2026 (Dan: „am deschis-o în birou și nu vedeam pașii din hală"): linia, ora,
        // câmpurile scrise, pozele și cronometrul vin de pe server — nu pornesc de la zero
        document.getElementById('intv-rpt-line')!.textContent = log.line_id || '—';
        document.getElementById('intv-rpt-datetime')!.textContent = log.start_time ? formatDateTimeRO(new Date(log.start_time)) : '—';
        _intvResetSincronizare();
        _intvMediaFiles = [];
        _intvAplicaDinServer(log, true);
        _intvMarcheazaZiNoua(log);
        _intvRandeazaMedia(log.media || []);
        _intvPornesteSincronizare();
        _mountUrgentEnrichmentsPanel();
        await workRefreshEnrichmentsPanel('maintenance_log', logId, 'intv');
        urgShowPhase('urg-phase-intervention');
        _showActiveInterventionBanner();
        const problemLine = (log.action || '').split('\n')[0];
        _loadSimilarInterventions(log.machine_id || '', problemLine);
        _loadColleagueBrief(log.id, log.machine_id || '', problemLine);
        toast(WORK_LIFECYCLE_LABELS.resumeUrgent, 'success');
    } catch (e: unknown) {
        toast('Eroare: ' + ((e as Error).message || String(e)), 'error');
    }
}

export function urgUpdateProgress(resultEl: HTMLElement|null) {
    const done = resultEl!.querySelectorAll('.urg-step-cb:checked').length;
    const total = resultEl!.querySelectorAll('.urg-step-cb').length;
    const pct = total ? Math.round(done / total * 100) : 0;
    const fill = resultEl!.querySelector('.urg-progress-fill');
    const lbl = resultEl!.querySelector('.urg-progress-label');
    if (fill) (fill as HTMLElement).style.width = pct + '%';
    if (lbl) lbl.textContent = done + ' din ' + total + ' pași finalizați (' + pct + '%)';
}

document.getElementById('urgent-guide-btn')?.addEventListener('click', async () => {
    const machine = ($('#jrn-machine') as HTMLInputElement)?.value;
    const operator = ($('#jrn-operator') as HTMLInputElement)?.value;
    const problem = ($('#jrn-action') as HTMLInputElement)?.value;
    if (!machine) { toast('Selectează mașina!', 'warning'); $('#jrn-machine')?.focus(); return; }
    if (!operator) { toast('Scrie numele tău!', 'warning'); $('#jrn-operator')?.focus(); return; }
    if (!problem || problem.length < 5) { toast('Descrie problema (minim 5 caractere)', 'warning'); $('#jrn-action')?.focus(); return; }

    const guideBtn = document.getElementById('urgent-guide-btn');
    const origText = guideBtn!.textContent;
    guideBtn!.textContent = '⏳ Se analizează...';
    (guideBtn as HTMLInputElement).disabled = true;
    const btnText = null;
    const btnHint = null;

    try {
        let feedbackContext = '';
        try {
            const pastLogs: RandMaintenanceLog[] = await api('/api/maintenance_log?machine_id=' + encodeURIComponent(machine) + '&category=urgent');
            const validated = (Array.isArray(pastLogs) ? pastLogs : []).filter(lg => {
                if (lg.validation_status !== 'validated' && lg.validation_status !== 'rejected') return false;
                try { const j = JSON.parse(lg.notes!); return j.type === 'ai_guided'; } catch(e) { return false; }
            }).slice(0, 3);
            if (validated.length > 0) {
                feedbackContext = '\n\nFEEDBACK DIN INTERVENȚII ANTERIOARE PE ACEASTĂ MAȘINĂ:';
                validated.forEach(lg => {
                    try {
                        const j = JSON.parse(lg.notes!);
                        const bad = (j.steps||[]).filter((s: PasSalvat) => s.feedback === 'bad' || s.deleted);
                        const good = (j.steps||[]).filter((s: PasSalvat) => s.feedback === 'good');
                        if (bad.length > 0) feedbackContext += '\n- Pași INCORECȚI/ELIMINAȚI (NU repeta): ' + bad.map((s: PasSalvat)=>s.text).join('; ');
                        if (good.length > 0) feedbackContext += '\n- Pași CORECȚI (poți reutiliza): ' + good.map((s: PasSalvat)=>s.text).join('; ');
                        if (j.engineer_notes) feedbackContext += '\n- Observații inginer: ' + j.engineer_notes;
                        if (lg.validation_notes) feedbackContext += '\n- Validare: ' + lg.validation_notes;
                    } catch(e) {}
                });
            }
        } catch(e) {}

        const query = 'Mașina: ' + machine + '.\nProblema raportată de operator (planul trebuie să rezolve EXACT această problemă): "' + problem + '"' +
            feedbackContext +
            '\n\nINSTRUCȚIUNI STRICTE PENTRU FORMAT:' +
            '\n- Răspunde DOAR cu pași numerotați (1. 2. 3. etc.).' +
            '\n- Primul rând al răspunsului trebuie să fie pasul 1 — FĂRĂ frază introductivă, FĂRĂ rezumat al datelor din sistem, FĂRĂ note finale.' +
            '\n- Toți pașii trebuie să diagnosticheze sau să rezolve problema raportată mai sus. NU include proceduri generice de siguranță, localizarea butoanelor de oprire de urgență sau intervale de mentenanță programată, DECÂT dacă problema raportată se referă explicit la ele.' +
            '\n- Fiecare pas trebuie să fie O SINGURĂ ACȚIUNE CONCRETĂ pe care operatorul o poate executa fizic.' +
            '\n- Fiecare pas să includă: CE unealtă/componentă, UNDE exact pe mașină, CUM se face acțiunea.' +
            '\n- Folosește limbaj simplu, fără termeni tehnici complecși.' +
            '\n- Maxim 12 pași.' +
            '\n- NU include titluri, secțiuni, explicații sau note.' +
            '\n- Exemplu de pas bun: "Oprește mașina de la butonul roșu STOP de pe panoul principal."' +
            '\n- Exemplu de pas rău: "Verificați sistemul hidraulic." (prea vag)';

        const res = await api('/api/chat', {
            method: 'POST',
            headers: {'Content-Type':'application/json'},
            body: JSON.stringify({ message: query, machine_id: machine, context_module: 'maintenance' })
        });
        const answer = res.reply || res.response || '';
        if (!answer) { toast('Nu s-a putut genera ghidarea. Încearcă din nou.', 'error'); return; }

        const lines = answer.split('\n').filter((l: string) => l.trim());
        // Only numbered/bulleted lines are actionable steps. Prose (intro like
        // "Pe baza intervalelor...", trailing notes, "> ⚠️" blockquotes) must
        // NOT become checkable steps — incident 8 iul 2026 pe SFM12LD.
        const stepLineRe = /^(?:\*{0,2}\d+[\.\)]|[-•]\s)/;
        let steps: string[] = [];
        lines.forEach((l: string) => {
            const trimmed = l.trim();
            if (!stepLineRe.test(trimmed)) return;
            const t = trimmed.replace(/^\*{0,2}(\d+)[\.\)]\*{0,2}\s*/, '').replace(/^[-•]\s*/, '').replace(/^\*\*.*?\*\*:?\s*/, '').trim();
            if (t.length < 10) return;
            if (/^(---|\*\*\*|===)/.test(trimmed)) return;
            steps.push(t);
        });
        // Defensive fallback: if the model ignored numbering entirely, keep the
        // old prose-line behaviour rather than showing an empty plan.
        if (steps.length === 0) {
            lines.forEach((l: string) => {
                const trimmed = l.trim();
                if (/^(#{1,3}\s|>|---|\*\*\*|===)/.test(trimmed)) return;
                if (/^\*\*[^*]+\*\*:?$/.test(trimmed)) return;
                const t = trimmed.replace(/^[-•]\s*/, '').replace(/^\*\*.*?\*\*:?\s*/, '').trim();
                if (t.length >= 10) steps.push(t);
            });
        }

        let html = '<div class="urg-result-layout">';

        html += '<div class="urg-result-main">';
        html += '<div class="urg-result-card">';
        html += '<div class="urg-result-header">';
        html += '<span style="font-size:1.8rem">🔧</span>';
        html += '<div class="urg-result-title">Plan de intervenție — ' + escHtml(machine) + '</div>';
        html += '</div>';
        html += '<p style="color:var(--text-muted);margin:0 0 12px;font-size:.95rem">Bifează fiecare pas după ce l-ai executat.</p>';
        html += '<div class="urg-progress-bar"><div class="urg-progress-fill" style="width:0%"></div></div>';
        html += '<div class="urg-progress-label" style="font-size:.9rem;font-weight:600;color:var(--accent);margin-bottom:12px">0 din ' + steps.length + ' pași finalizați (0%)</div>';
        html += '<div class="urg-steps-scroll">';
        steps.forEach((s, i) => {
            html += '<div class="urg-step-item" data-idx="' + (i+1) + '" data-feedback="" data-original="' + escHtml(s) + '">' +
                '<input type="checkbox" class="urg-step-cb" data-step="' + (i+1) + '">' +
                '<span class="urg-step-num">' + (i+1) + '.</span>' +
                '<span class="urg-step-text">' + colleagueMd(s) + '</span>' +
                '<span class="urg-step-actions">' +
                '<button type="button" class="urg-fb-btn urg-fb-good" title="Pas corect" data-fb="good">👍</button>' +
                '<button type="button" class="urg-fb-btn urg-fb-bad" title="Pas incorect/neaplicabil" data-fb="bad">👎</button>' +
                '<button type="button" class="urg-fb-btn urg-fb-del" title="Elimină pasul" data-fb="delete">❌</button>' +
                '</span></div>';
        });
        html += '</div>';
        html += '<div style="display:flex;gap:10px;margin-top:16px;flex-wrap:wrap">';
        html += '<button type="button" class="urg-btn-main" id="urg-save-btn" style="flex:1"><span class="urg-btn-text">💾 SALVEAZĂ INTERVENȚIA</span></button>';
        html += '<button type="button" class="urg-btn-alt" id="urg-back-btn" style="padding:14px 20px">← Înapoi</button>';
        html += '</div></div></div>';

        html += '<div class="urg-result-side">';
        html += '<div class="urg-side-card urg-side-help">';
        html += '<h4>📞 Ajutor de la distanță</h4>';
        html += '<p>Nu te descurci? Solicită ghidare de la un inginer.</p>';
        html += '<button type="button" class="urg-btn-help" id="urg-call-help">SOLICITĂ AJUTOR INGINER</button>';
        html += '</div>';
        html += '<div class="urg-side-card" id="urg-similar-container">';
        html += '<h4>📋 Intervenții anterioare</h4>';
        html += '<p style="color:var(--text-muted);font-size:.85rem">Se încarcă...</p>';
        html += '</div>';
        html += '<div class="urg-side-card urg-side-info">';
        html += '<h4>💡 Sfaturi</h4>';
        html += '<ul style="margin:0;padding-left:18px;color:var(--text-muted);font-size:.88rem;line-height:1.6">';
        html += '<li>Bifează pașii pe măsură ce îi execuți</li>';
        html += '<li>Folosește 👍/👎 ca să evaluezi fiecare pas</li>';
        html += '<li>Dacă un pas e greșit, apasă ❌ să-l elimini</li>';
        html += '<li>La final, salvează pentru validarea inginerului</li>';
        html += '</ul></div></div>';

        html += '</div>';

        const resultEl = document.getElementById('urgent-guide-result');
        resultEl!.innerHTML = html;
        urgShowPhase('urg-phase-2');

        resultEl!.querySelectorAll('.urg-step-cb').forEach(cb => {
            cb.addEventListener('change', () => {
                const row = cb.closest('.urg-step-item');
                row!.classList.toggle('done', (cb as HTMLInputElement).checked);
                urgUpdateProgress(resultEl);
            });
        });
        resultEl!.querySelectorAll('.urg-step-item').forEach(row => {
            row.addEventListener('click', (e) => {
                if ((e.target as HTMLElement).closest('.urg-fb-btn') || (e.target as HTMLElement).closest('.urg-step-cb')) return;
                const cb = row.querySelector('.urg-step-cb');
                if (cb) { (cb as HTMLInputElement).checked = !(cb as HTMLInputElement).checked; cb.dispatchEvent(new Event('change')); }
            });
        });
        resultEl!.querySelectorAll('.urg-fb-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const row = btn.closest('.urg-step-item');
                const fb = (btn as HTMLElement).dataset.fb;
                if (fb === 'delete') {
                    (row as HTMLElement).style.opacity = '0.3';
                    (row as HTMLElement).style.textDecoration = 'line-through';
                    (row as HTMLElement).dataset.feedback = 'deleted';
                    (row!.querySelector('.urg-step-cb') as HTMLInputElement).checked = false;
                    (row!.querySelector('.urg-step-cb') as HTMLInputElement).disabled = true;
                    row!.classList.add('urg-deleted');
                    urgUpdateProgress(resultEl);
                    toast('Pas eliminat', 'info');
                } else {
                    row!.querySelectorAll('.urg-fb-btn').forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');
                    (row as HTMLElement).dataset.feedback = fb;
                    toast(fb === 'good' ? '👍 Pas marcat ca bun' : '👎 Pas marcat ca incorect', 'info');
                }
            });
        });

        document.getElementById('urg-back-btn')?.addEventListener('click', () => urgShowPhase('urg-phase-1'));

        document.getElementById('urg-save-btn')?.addEventListener('click', async () => {
            const allRows = resultEl!.querySelectorAll('.urg-step-item:not(.urg-deleted)');
            const checked = resultEl!.querySelectorAll('.urg-step-item:not(.urg-deleted) .urg-step-cb:checked').length;
            const total = allRows.length;
            if (total > 0 && checked < total && !await customConfirm('Doar ' + checked + ' din ' + total + ' pași completați.\nSalvezi oricum intervenția?', '⚠ Pași incompleți')) return;

            const stepsData: PasSalvat[] = [];
            resultEl!.querySelectorAll('.urg-step-item').forEach(row => {
                stepsData.push({
                    step: parseInt((row as HTMLElement).dataset.idx!),
                    text: (row as HTMLElement).dataset.original || row.querySelector('.urg-step-text')?.textContent || '',
                    completed: (row.querySelector('.urg-step-cb') as HTMLInputElement)?.checked || false,
                    feedback: (row as HTMLElement).dataset.feedback || '',
                    deleted: row.classList.contains('urg-deleted')
                });
            });

            try {
                const salvat = await api('/api/maintenance_log', {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({
                        machine_id: machine, date: todayStr(), action: problem,
                        technician: operator, parts_used: '', hours_at_service: 0,
                        notes: JSON.stringify({
                            type: 'ai_guided',
                            steps_completed: checked,
                            steps_total: total,
                            steps: stepsData
                        }),
                        category: 'urgent'
                    })
                });
                toast(salvat && salvat.validation_status === 'validated'
                    ? '✅ Intervenția a fost salvată și validată pe numele tău.'
                    : '✅ Intervenția a fost salvată! Așteaptă validarea inginerului.', 'success');
                urgShowPhase('urg-phase-1');
                ($('#jrn-action') as HTMLInputElement).value = '';
                loadJournalLog();
            } catch(e) { toast('Eroare la salvare: ' + (e as Error).message, 'error'); }
        });

        document.getElementById('urg-call-help')?.addEventListener('click', async () => {
            const helpBtn = document.getElementById('urg-call-help');
            (helpBtn as HTMLInputElement).disabled = true; helpBtn!.textContent = '⏳ Se trimite...';
            const stepsInfo = resultEl!.querySelectorAll('.urg-step-cb:checked').length + '/' + resultEl!.querySelectorAll('.urg-step-cb').length;
            const msg = 'Mașina: ' + machine + '\nProblema: ' + problem + '\nOperator: ' + operator + '\nPași completați: ' + stepsInfo;
            try {
                const res = await api('/api/help_requests', {
                    method: 'POST', headers: {'Content-Type':'application/json'},
                    body: JSON.stringify({ machine_id: machine, requested_by: operator, message: msg })
                });
                const helpHtml = '<div style="background:var(--card-bg);border:2px solid var(--green);border-radius:12px;padding:16px">' +
                    '<h4 style="color:var(--green);margin:0 0 8px">✅ Cerere #' + (res.id || '') + ' trimisă</h4>' +
                    '<p style="color:var(--text-muted);margin:0 0 6px;font-size:.9rem">Inginerul a fost notificat. Verifică periodic pentru răspuns.</p>' +
                    '<button type="button" class="btn btn-sm" id="urg-check-help" data-req-id="' + (res.id || '') + '">🔄 Verifică răspuns</button>' +
                    '<div id="urg-help-response"></div></div>';
                const container = document.getElementById('urg-similar-container');
                if (container) container.insertAdjacentHTML('afterbegin', helpHtml);
                document.getElementById('urg-check-help')?.addEventListener('click', async () => {
                    const reqId = document.getElementById('urg-check-help')!.dataset.reqId;
                    const reqs = await api('/api/help_requests?status=').catch(() => []);
                    const found = (Array.isArray(reqs) ? reqs : []).find(r => String(r.id) === String(reqId));
                    const respDiv = document.getElementById('urg-help-response');
                    if (found && found.status === 'responded' && found.response) {
                        respDiv!.innerHTML = '<div style="margin-top:10px;padding:12px;background:rgba(0,200,255,.06);border:1px solid var(--cyan);border-radius:8px">' +
                            '<strong style="color:var(--cyan)">Răspuns de la ' + escHtml(found.responded_by || 'inginer') + ':</strong>' +
                            '<p style="margin:6px 0 0;white-space:pre-wrap;font-size:.95rem">' + escHtml(found.response) + '</p></div>';
                    } else {
                        respDiv!.innerHTML = '<p style="margin-top:8px;color:var(--text-muted);font-size:.9rem">⏳ Încă niciun răspuns. Încearcă din nou peste câteva minute.</p>';
                    }
                });
                toast('📞 Cerere de ajutor trimisă!', 'success');
                _updateHelpBadge();
            } catch(e) {
                toast('Eroare la trimiterea cererii: ' + (e as Error).message, 'error');
                (helpBtn as HTMLInputElement).disabled = false; helpBtn!.textContent = 'SOLICITĂ AJUTOR INGINER';
            }
        });

        try {
            const simRes = await api('/api/maintenance_log?machine_id=' + encodeURIComponent(machine) + '&limit=5');
            const logs = simRes.logs || simRes || [];
            if (Array.isArray(logs) && logs.length > 0) {
                let simHtml = '<h4>📋 Intervenții pe ' + escHtml(machine) + '</h4><div class="urg-similar-box">';
                logs.slice(0, 5).forEach(lg => {
                    const lid = lg.id || '';
                    simHtml += '<div class="urg-similar-item urg-similar-clickable" data-log-id="' + lid + '">' +
                        '<strong>' + escHtml(lg.date || '') + '</strong> — ' + escHtml(lg.action || '') +
                        (lg.technician ? ' <span style="color:var(--text-muted)">(de: ' + escHtml(lg.technician) + ')</span>' : '') +
                        '<span class="urg-similar-open">👁 Deschide</span></div>';
                });
                simHtml += '</div>';
                const sc = document.getElementById('urg-similar-container');
                if (sc && !sc.querySelector('.urg-similar-box')) {
                    sc.insertAdjacentHTML('beforeend', simHtml);
                    sc.querySelectorAll('.urg-similar-clickable').forEach(el => {
                        el.addEventListener('click', () => {
                            const logId = (el as HTMLElement).dataset.logId;
                            if (logId) openLogDetail(logId);
                        });
                    });
                }
            }
        } catch(e) {}

    } catch(e) {
        toast('Eroare: ' + (e as Error).message, 'error');
    }
    guideBtn!.textContent = origText;
    (guideBtn as HTMLInputElement).disabled = false;
});

/* ── BANNER REVENIRE INTERVENȚIE ACTIVĂ ── */
export function _showActiveInterventionBanner() {
    let banner = document.getElementById('intv-active-banner');
    if (!_intvLogId) {
        if (banner) banner.remove();
        return;
    }
    const journalSec = document.getElementById('sec-journal');
    const isOnJournal = journalSec && journalSec.classList.contains('active');
    if (isOnJournal) {
        if (banner) banner.style.display = 'none';
        return;
    }
    if (!banner) {
        banner = document.createElement('div');
        banner.id = 'intv-active-banner';
        banner.style.cssText = 'position:fixed;bottom:20px;left:50%;transform:translateX(-50%);z-index:1100;background:linear-gradient(135deg,#dc2626,#b91c1c);color:#fff;padding:12px 24px;border-radius:14px;box-shadow:0 8px 30px rgba(220,38,38,.4);display:flex;align-items:center;gap:14px;cursor:pointer;animation:slideUp .3s ease;font-family:inherit';
        banner.innerHTML = `
            <span style="font-size:1.3rem">🚨</span>
            <div>
                <div style="font-weight:700;font-size:.95rem">Intervenție activă</div>
                <div id="intv-banner-info" style="font-size:.82rem;opacity:.85"></div>
            </div>
            <button style="padding:8px 18px;border-radius:10px;border:2px solid rgba(255,255,255,.4);background:rgba(255,255,255,.15);color:#fff;font-weight:700;font-size:.9rem;cursor:pointer;white-space:nowrap">↩ Revino</button>`;
        banner.addEventListener('click', () => {
            const navLink = document.querySelector('.nav-links a[data-section="journal"]') as HTMLElement;
            if (navLink) navLink.click();
            setTimeout(() => {
                urgShowPhase('urg-phase-intervention');
                banner!.style.display = 'none';
            }, 100);
        });
        document.body.appendChild(banner);
    }
    const infoEl = banner.querySelector('#intv-banner-info');
    if (infoEl) infoEl.textContent = _intvRegNumber || 'Cronometru activ';
    banner.style.display = 'flex';
}

/* ── ÎNREGISTRARE INTERVENȚIE (buton principal) ── */
export let _intvTimerInterval: ReturnType<typeof setInterval> | null = null;
export let _intvStartTime: number | null = null;
export let _intvElapsed = 0;
export let _intvPaused = false;
export let _intvLogId: number | null = null;
export let _intvRegNumber = '';

export function _intvStartTimer() {
    _intvSeteazaCronometru(0, true);
}

function _intvAfiseazaCronometru(): void {
    const timerEl = document.getElementById('intv-timer');
    const ms = _intvPaused ? _intvElapsed : (Date.now() - _intvStartTime! + _intvElapsed);
    const s = Math.max(0, Math.floor(ms / 1000));
    const h = String(Math.floor(s / 3600)).padStart(2, '0');
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    if (timerEl) timerEl.textContent = `${h}:${m}:${sec}`;
}

/** Cronometrul pornește de la timpul deja lucrat (de pe server, suma bucăților dintre pauze) —
 *  la redeschidere, pe orice dispozitiv, nu mai pornește de la 00:00 (30 sep 2026). */
export function _intvSeteazaCronometru(secunde: number, activ: boolean) {
    _intvStartTime = Date.now();
    _intvElapsed = Math.max(0, secunde) * 1000;
    _intvPaused = !activ;
    const timerEl = document.getElementById('intv-timer');
    if (timerEl) timerEl.classList.toggle('paused', !activ);
    const bp = document.getElementById('intv-timer-pause'), br = document.getElementById('intv-timer-resume');
    if (bp) bp.style.display = activ ? '' : 'none';
    if (br) br.style.display = activ ? 'none' : '';
    clearInterval(_intvTimerInterval ?? undefined);
    _intvAfiseazaCronometru();
    _intvTimerInterval = setInterval(() => { if (!_intvPaused) _intvAfiseazaCronometru(); }, 1000);
}

/* ── Intervenția urgentă ținută pe server, sincronizată între dispozitive (30 sep 2026) ─────────
 * Dan: „am pornit o intervenție pe tabletă în hală, am vrut s-o deschid în birou să completez —
 * s-a deschis din nou, nu vedeam pașii din hală". Tot ce se scria stătea doar în pagina tabletei
 * până la „Salvează" (iar „Pauză" pierdea textul). Acum: fiecare câmp pleacă la server la 1,5 s
 * după ce omul se oprește din scris; la 10 s fiecare dispozitiv preia ce au scris ceilalți — fără
 * să atingă câmpul în care scrie el; pozele se încarcă imediat ce sunt făcute. */
const _INTV_CAMPURI: Array<[string, CampIntv]> = [
    ['intv-persons', 'involved_persons'], ['intv-constatari', 'constatari'], ['intv-actions-done', 'actiuni'], ['intv-cause', 'cause_found'],
    ['jrn-parts', 'parts_used'], ['jrn-notes', 'notes'],
];
export const _intvSync = { murdare: new Set<string>(), tSalvare: 0 as ReturnType<typeof setTimeout>, tPoll: 0 as ReturnType<typeof setInterval>, legat: false, inCurs: false, nrMedia: -1, tastat: {} as Record<string, number> };

function _intvResetSincronizare(): void {
    clearTimeout(_intvSync.tSalvare);
    clearInterval(_intvSync.tPoll);
    _intvSync.murdare.clear();
    _intvSync.nrMedia = -1;
    _intvStareSalvare('');
}
function _intvGolesteFormular(): void {
    _INTV_CAMPURI.forEach(([id]) => { const el = document.getElementById(id) as HTMLInputElement | null; if (el) el.value = ''; });
}
function _intvStareSalvare(text: string, rau = false): void {
    const el = document.getElementById('intv-sync-stare');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('rau', rau);
}
/** Trimite la server câmpurile schimbate; întoarce false dacă n-a reușit. */
export async function _intvSalveazaCiorna(): Promise<boolean> {
    clearTimeout(_intvSync.tSalvare);
    if (!_intvLogId || !_intvSync.murdare.size) return true;
    if (_intvSync.inCurs) { _intvProgrameazaSalvare(); return false; }
    const trimis: Record<string, string> = {};
    _INTV_CAMPURI.forEach(([id, cheie]) => {
        if (_intvSync.murdare.has(cheie)) trimis[cheie] = (document.getElementById(id) as HTMLInputElement | null)?.value ?? '';
    });
    _intvSync.inCurs = true;
    try {
        await api('/api/maintenance_log/' + _intvLogId + '/ciorna', {
            method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(trimis),
        });
        _INTV_CAMPURI.forEach(([id, cheie]) => {
            // câmpul se poate fi schimbat din nou cât a mers cererea — rămâne „de trimis"
            if (cheie in trimis && ((document.getElementById(id) as HTMLInputElement | null)?.value ?? '') === trimis[cheie]) _intvSync.murdare.delete(cheie);
        });
        _intvStareSalvare('✓ Salvat ' + new Date().toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
        if (_intvSync.murdare.size) _intvProgrameazaSalvare();
        return true;
    } catch (_e) {
        _intvStareSalvare('⚠ Nesalvat — reîncerc (rămâne scris aici)', true);
        _intvSync.tSalvare = setTimeout(() => { _intvSalveazaCiorna(); }, 5000);
        return false;
    } finally {
        _intvSync.inCurs = false;
    }
}
function _intvProgrameazaSalvare(): void {
    clearTimeout(_intvSync.tSalvare);
    _intvSync.tSalvare = setTimeout(() => { _intvSalveazaCiorna(); }, 1500);
}
function _intvLeagaCampuri(): void {
    if (_intvSync.legat) return;
    _intvSync.legat = true;
    _INTV_CAMPURI.forEach(([id, cheie]) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.addEventListener('input', () => {
            if (!_intvLogId) return;
            _intvSync.tastat[cheie] = Date.now();
            _intvSync.murdare.add(cheie);
            _intvStareSalvare('… se salvează');
            _intvProgrameazaSalvare();
        });
        el.addEventListener('blur', () => { if (_intvLogId && _intvSync.murdare.size) _intvSalveazaCiorna(); });
    });
}
function _intvValoareServer(log: JurnalDetaliu, cheie: CampIntv): string {
    if (cheie === 'actiuni') {
        const a = log.interventie?.actiuni;
        if (a) return a;
        const m = String(log.action || '').match(/\n\nAcțiuni:[ \n]([\s\S]*)$/);
        return m ? m[1] : '';
    }
    if (cheie === 'constatari') {
        const c = log.interventie?.constatari;
        if (c) return c;
        const m = String(log.action || '').match(/\n\nConstatări:[ \n]([\s\S]*?)(?:\n\nAcțiuni:[ \n]|$)/);
        return m ? m[1] : '';
    }
    return log[cheie] || '';
}

/** O secțiune a raportului final: fără titluri de zi rămase goale; titlul de zi pe rândul lui. */
function _intvSectiune(eticheta: string, text: string): string {
    let t = (text || '').replace(/(?:\n*— [^\n]+ —\s*)+$/, '').trim();
    t = t.replace(/— [^\n]+ —\s*\n(?=\s*— [^\n]+ —)/g, '');   // zi fără nimic scris între două titluri
    if (!t) return '';
    return '\n\n' + eticheta + ':' + (t.startsWith('—') ? '\n' : ' ') + t;
}

/* Zi nouă de lucru pe aceeași intervenție (Dan, 30 sep: „se desface mâine matrița — o defecțiune cu
 * mai multe operații"): la redeschidere într-o altă zi, constatările și operațiile primesc data ca
 * titlu, ca raportul final să arate ce s-a găsit / făcut în fiecare zi. Prima zi primește titlul
 * ei retroactiv, când apare a doua. */
function _intvTitluZi(d: Date): string {
    return '— ' + d.toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' }) + ' —';
}
function _intvMarcheazaZiNoua(log: JurnalDetaliu): void {
    const salvat = Date.parse(log.interventie?.salvat_la || '') || 0;
    if (!salvat) return;
    const zi = (t: number) => new Date(t).toLocaleDateString('ro-RO');
    if (zi(salvat) === zi(Date.now())) return;
    const azi = _intvTitluZi(new Date());
    const start = _intvTitluZi(new Date(Date.parse(log.start_time || '') || salvat));
    [['intv-constatari', 'constatari'], ['intv-actions-done', 'actiuni']].forEach(([id, cheie]) => {
        const el = document.getElementById(id) as HTMLTextAreaElement | null;
        if (!el || el.value.includes(azi)) return;
        const text = el.value.replace(/\s+$/, '');
        if (!text) return;
        const cuTitluri = /^— .+ —$/m.test(text) ? text : (start + '\n' + text);
        el.value = cuTitluri + '\n\n' + azi + '\n';
        _intvSync.murdare.add(cheie);
    });
    if (_intvSync.murdare.size) _intvProgrameazaSalvare();
}
/** Ce e pe server intră în formular — `tot` la deschidere; altfel doar câmpurile pe care omul nu
 *  le editează acum: fără schimbări netrimise și fără tastare în ultimele 8 s. (Doar „are cursorul"
 *  nu ajunge: pe tableta lăsată pe masă, cursorul rămâne în ultimul câmp și nu primea nimic.) */
function _intvAplicaDinServer(log: JurnalDetaliu, tot = false): void {
    _INTV_CAMPURI.forEach(([id, cheie]) => {
        const el = document.getElementById(id) as HTMLInputElement | null;
        if (!el) return;
        const scrieAcum = document.activeElement === el && Date.now() - (_intvSync.tastat[cheie] || 0) < 8000;
        if (!tot && (_intvSync.murdare.has(cheie) || scrieAcum)) return;
        const v = _intvValoareServer(log, cheie);
        if (el.value !== v) el.value = v;
    });
    const sec = log.interventie?.lucrat_secunde;
    if (sec != null) {
        const activ = log.status === 'active';
        const localS = (_intvPaused ? _intvElapsed : (Date.now() - (_intvStartTime || Date.now()) + _intvElapsed)) / 1000;
        // se reface doar dacă s-a schimbat starea (pauză pe alt dispozitiv) sau diferă vizibil
        if (tot || activ === _intvPaused || Math.abs(localS - sec) > 5) _intvSeteazaCronometru(sec, activ);
    }
}
function _intvPornesteSincronizare(): void {
    _intvLeagaCampuri();
    clearInterval(_intvSync.tPoll);
    _intvSync.tPoll = setInterval(async () => {
        const faza = document.getElementById('urg-phase-intervention');
        if (!_intvLogId || !faza || faza.style.display === 'none') return;
        try {
            const log: JurnalDetaliu = await api('/api/maintenance_log/' + _intvLogId);
            if (log.status !== 'active' && log.status !== 'paused') {
                // închisă pe alt dispozitiv (finalizată / anulată / validată)
                _intvResetSincronizare();
                clearInterval(_intvTimerInterval ?? undefined);
                toast(log.status === 'cancelled' ? 'Intervenția a fost anulată pe alt dispozitiv.'
                    : 'Intervenția a fost finalizată pe alt dispozitiv.', 'info');
                _intvLogId = null;
                _showActiveInterventionBanner();
                urgShowPhase('urg-phase-1');
                return;
            }
            _intvAplicaDinServer(log);
            if ((log.media || []).length !== _intvSync.nrMedia) _intvRandeazaMedia(log.media || []);
            // poze rămase neîncărcate (rețea / sesiune expirată în hală) — se reîncearcă singure
            if (_intvMediaFiles.length) _intvIncarcaMediaAcum();
            if (_intvSync.murdare.size) _intvSalveazaCiorna();
        } catch (_e) { /* rețea căzută: următoarea încercare peste 10 s */ }
    }, 10000);
}

export function _intvGetDurationMinutes() {
    if (!_intvStartTime) return 0;
    const totalMs = _intvPaused ? _intvElapsed : (Date.now() - _intvStartTime + _intvElapsed);
    return Math.round(totalMs / 60000);
}

document.getElementById('intv-timer-pause')?.addEventListener('click', async () => {
    _intvPaused = true;
    _intvElapsed += Date.now() - _intvStartTime!;
    const timerEl = document.getElementById('intv-timer');
    if (timerEl) timerEl.classList.add('paused');
    document.getElementById('intv-timer-pause')!.style.display = 'none';
    document.getElementById('intv-timer-resume')!.style.display = '';
    // pauza se vede pe toate dispozitivele (și timpul lucrat se oprește pe server)
    if (_intvLogId) {
        await _intvSalveazaCiorna();
        try { await workPause('maintenance_log', _intvLogId, {}); } catch (_e) { /* rămâne locală */ }
    }
});

// 🎥 Filmează intervenția URGENTĂ (camere POV, 2026-07-29): urgențele au
// log-ul creat DIN START (_intvLogId) → filmarea se leagă DIRECT de
// intervenție, fără potrivirea pe zi. Exact cazurile rare care merită arhivate.
document.getElementById('intv-filmeaza-btn')?.addEventListener('click', () => {
    const logId = _intvLogId || (document.getElementById('intv-log-id') as HTMLInputElement)?.value || '';
    const mid = document.getElementById('intv-machine-label')?.textContent || '';
    const qs = new URLSearchParams();
    if (logId) qs.set('log_id', String(logId));
    if (mid) qs.set('machine_id', mid);
    const pref = location.pathname.replace(/\/(index\.html)?$/, '');   // prin hub: /maint
    window.open(pref + '/live' + (qs.toString() ? ('?' + qs.toString()) : ''), '_blank');
});

document.getElementById('intv-timer-resume')?.addEventListener('click', () => {
    if (_intvLogId) workResume('maintenance_log', _intvLogId).catch(() => {});
    _intvPaused = false;
    _intvStartTime = Date.now();
    const timerEl = document.getElementById('intv-timer');
    if (timerEl) timerEl.classList.remove('paused');
    document.getElementById('intv-timer-pause')!.style.display = '';
    document.getElementById('intv-timer-resume')!.style.display = 'none';
});

document.getElementById('urgent-register-btn')?.addEventListener('click', async () => {
    const lineVal = ($('#jrn-line') as HTMLInputElement)?.value;
    const machine = ($('#jrn-machine') as HTMLInputElement)?.value;
    const operator = ($('#jrn-operator') as HTMLInputElement)?.value;
    const problem = ($('#jrn-action') as HTMLInputElement)?.value;
    if (!lineVal) { toast('Selectează linia!', 'warning'); $('#jrn-line')?.focus(); return; }
    if (!machine) { toast('Selectează utilajul!', 'warning'); $('#jrn-machine')?.focus(); return; }
    if (!operator) { toast('Scrie numele tău!', 'warning'); $('#jrn-operator')?.focus(); return; }
    if (!problem || problem.length < 3) { toast('Descrie problema!', 'warning'); $('#jrn-action')?.focus(); return; }

    // Corectura scrierii (1 nov 2026, Dan): problema se vede corectată și se aprobă înainte de înregistrare
    const problemaCorecta = await confirmaUnText('Problema', problem, 'interventie-semnalare');
    const jrnAction = $('#jrn-action') as HTMLInputElement | null;
    if (jrnAction) jrnAction.value = problemaCorecta;
    const lineId = lineVal;
    const lineLabel = ($('#jrn-line') as HTMLSelectElement)?.selectedOptions[0]?.textContent || lineVal;
    const now = new Date();
    // 🌙 Ziua LOCALĂ, nu UTC (audit 2026-08-19, constatarea 2.7): `toISOString()` dă ora UTC,
    // așa că între 00:00 și 03:00 ora locală — exact tura de noapte — ziua extrasă era cea de IERI.
    // Două urgențe reale au intrat greșit în registru cu 2026-08-18: INT-01648 (M3, reparată la 00:10)
    // și INT-01654 (M4, la 02:29). Urgența e singura care primește număr de registru, deci avaria
    // nu apărea nici în „ce s-a întâmplat azi", nici la predarea de tură.
    const dateStr = todayStr();
    const timeStr = formatDateTimeRO(now);
    // start_time rămâne moment exact (ISO complet cu oră) — aici UTC e corect, nu se extrage ziua.
    const startTime = now.toISOString();

    try {
        const res = await api('/api/maintenance_log', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                machine_id: machine, date: dateStr, action: problemaCorecta,
                technician: operator, category: 'urgent',
                start_time: startTime, status: 'active', line_id: lineId,
            })
        });
        _intvLogId = res.id;
        _intvRegNumber = res.registration_number || '';
    } catch (e) {
        toast('Eroare la înregistrare: ' + (e as Error).message, 'error');
        return;
    }

    (document.getElementById('intv-log-id') as HTMLInputElement).value = String(_intvLogId ?? '');   // ca setter-ul: null → ''
    const regEl = document.getElementById('intv-reg-number');
    if (regEl) regEl.textContent = _intvRegNumber;
    const machineLabel = ($('#jrn-machine') as HTMLSelectElement)?.selectedOptions[0]?.textContent || machine;
    document.getElementById('intv-machine-label')!.textContent = machineLabel;
    document.getElementById('intv-problem-label')!.textContent = problem;
    document.getElementById('intv-rpt-machine')!.textContent = machineLabel;
    document.getElementById('intv-rpt-line')!.textContent = lineLabel || lineId || '—';
    document.getElementById('intv-rpt-problem')!.textContent = problem;
    document.getElementById('intv-rpt-reporter')!.textContent = operator;
    document.getElementById('intv-rpt-datetime')!.textContent = timeStr;

    _intvResetSincronizare();
    _intvGolesteFormular();
    _intvMediaFiles = [];
    _intvRandeazaMedia([]);
    if (document.getElementById('intv-persons')) {
        (document.getElementById('intv-persons') as HTMLInputElement).value = operator;
        _intvSync.murdare.add('involved_persons');
        _intvSalveazaCiorna();
    }

    urgShowPhase('urg-phase-intervention');
    _intvStartTimer();
    _intvPornesteSincronizare();
    _mountUrgentEnrichmentsPanel();
    if (_intvLogId) await workRefreshEnrichmentsPanel('maintenance_log', _intvLogId, 'intv');

    _loadSimilarInterventions(machine, problem);
    _loadColleagueBrief(_intvLogId, machine, problem);
});

export async function _loadSimilarInterventions(machine: string, problem: string) {
    const el = document.getElementById('intv-similar-list');
    if (!el) return;
    el.innerHTML = '<div class="urg-intv-loading">Se caută intervenții anterioare pe ' + escHtml(machine as string) + '...</div>';
    try {
        const items: LucrareSimilara[] = await api('/api/maintenance_log/similar?machine_id=' + encodeURIComponent(machine!) + '&problem=' + encodeURIComponent(problem));
        if (!items || items.length === 0) {
            el.innerHTML = '<div class="urg-intv-loading">Nu s-au găsit intervenții anterioare pe această mașină.</div>';
            return;
        }
        el.innerHTML = '';
        items.forEach((item) => {
            const div = document.createElement('div');
            div.className = 'urg-intv-similar-item' + (item.relevance > 0 ? ' urg-similar-relevant' : '');
            const hasRecording = item.recording_filename ? ' 🎥' : '';
            const validIcon = item.validation_status === 'validated' ? ' ✅' : (item.validation_status === 'rejected' ? ' ❌' : '');
            const dur = item.duration_minutes ? ` | ${item.duration_minutes} min` : '';

            div.innerHTML = `<div class="urg-sim-header">
                <div class="urg-intv-similar-date">${escHtml(item.date || '—')}${validIcon}${hasRecording}${dur}</div>
                <span class="urg-sim-expand-icon">▶</span>
            </div>
            <div class="urg-intv-similar-action">${escHtml((item.action || '').substring(0, 150))}</div>
            <div class="urg-intv-similar-operator">Operator: ${escHtml(item.technician || '—')}${item.parts_used ? ' | Piese: ' + escHtml(item.parts_used) : ''}${item.cause_found ? ' | Cauza: ' + escHtml(item.cause_found) : ''}</div>
            <div class="urg-sim-detail" style="display:none"></div>`;

            div.addEventListener('click', async () => {
                const detail = div.querySelector('.urg-sim-detail');
                const icon = div.querySelector('.urg-sim-expand-icon');
                if ((detail as HTMLElement).style.display !== 'none') {
                    (detail as HTMLElement).style.display = 'none';
                    icon!.textContent = '▶';
                    return;
                }
                icon!.textContent = '▼';
                (detail as HTMLElement).style.display = 'block';
                detail!.innerHTML = '<div class="urg-intv-loading">Se încarcă detalii...</div>';
                try {
                    const full: JurnalDetaliu = await api('/api/maintenance_log/' + item.id);
                    let dhtml = '';
                    if (full.action) dhtml += '<div class="urg-sim-section"><strong>Problemă / Acțiuni:</strong><p>' + escHtml(full.action) + '</p></div>';
                    if (full.notes) {
                        let notesDisplay: string | null = full.notes;
                        try {
                            const nj = JSON.parse(full.notes);
                            if (nj.steps && Array.isArray(nj.steps)) {
                                dhtml += '<div class="urg-sim-section"><strong>Pași efectuați:</strong><ol>';
                                nj.steps.forEach((s: PasSalvat) => {
                                    const fb = s.feedback === 'good' ? ' ✅' : (s.feedback === 'bad' ? ' ❌' : '');
                                    const del = s.deleted ? ' style="text-decoration:line-through;opacity:.5"' : '';
                                    dhtml += `<li${del}>${escHtml(s.text || '')}${fb}</li>`;
                                });
                                dhtml += '</ol></div>';
                                if (nj.engineer_notes) dhtml += '<div class="urg-sim-section"><strong>Note inginer:</strong><p>' + escHtml(nj.engineer_notes) + '</p></div>';
                                notesDisplay = null;
                            }
                        } catch(e) {}
                        if (notesDisplay) dhtml += '<div class="urg-sim-section"><strong>Observații:</strong><p>' + escHtml(notesDisplay) + '</p></div>';
                    }
                    if (full.cause_found) dhtml += '<div class="urg-sim-section"><strong>Cauza:</strong> ' + escHtml(full.cause_found) + '</div>';
                    if (full.involved_persons) dhtml += '<div class="urg-sim-section"><strong>Persoane:</strong> ' + escHtml(full.involved_persons) + '</div>';
                    if (full.duration_minutes) dhtml += '<div class="urg-sim-section"><strong>Durată:</strong> ' + full.duration_minutes + ' minute</div>';
                    if (full.recording_filename) {
                        const ext = (full.recording_filename || '').split('.').pop()!.toLowerCase();
                        const isVideo = ['mp4','webm','mov','avi','mkv'].includes(ext);
                        const isImage = ['jpg','jpeg','png','gif','webp'].includes(ext);
                        const mediaUrl = '/api/maintenance_log/' + item.id + '/recording';
                        if (isVideo) {
                            dhtml += '<div class="urg-sim-section"><strong>🎥 Filmare:</strong><br><video src="' + mediaUrl + '" controls style="max-width:100%;max-height:250px;border-radius:8px;margin-top:6px"></video></div>';
                        } else if (isImage) {
                            dhtml += '<div class="urg-sim-section"><strong>📸 Poză:</strong><br><img src="' + mediaUrl + '" style="max-width:100%;max-height:250px;border-radius:8px;margin-top:6px"></div>';
                        } else {
                            dhtml += '<div class="urg-sim-section"><a href="' + mediaUrl + '" target="_blank">📎 Descarcă fișier atașat</a></div>';
                        }
                    }
                    if (!dhtml) dhtml = '<div class="urg-intv-loading">Nu sunt detalii suplimentare.</div>';
                    detail!.innerHTML = dhtml;
                    // Transcrierile filmărilor POV (camere mecanici, 2026-07-29):
                    // se încarcă asincron ca să nu întârzie panoul
                    fetch('/api/maintenance_log/' + item.id + '/transcrieri')
                        .then(r => r.ok ? r.json() : [])
                        .then((trs: TranscriereFilmare[]) => {
                            const cuText = (trs || []).filter(t => t.transcript);
                            if (!cuText.length) return;
                            const bloc = document.createElement('div');
                            bloc.className = 'urg-sim-section';
                            bloc.innerHTML = '<strong>📝 Ce a spus mecanicul în timpul lucrului:</strong>' +
                                cuText.map(t =>
                                    '<pre style="white-space:pre-wrap;font-size:12px;background:var(--bg-2,#f5f5f5);' +
                                    'padding:8px;border-radius:6px;margin-top:6px;max-height:220px;overflow:auto">' +
                                    escHtml(t.transcript) + '</pre>').join('');
                            detail!.appendChild(bloc);
                        })
                        .catch(() => {});
                } catch(e) {
                    detail!.innerHTML = '<div class="urg-intv-loading">Eroare la încărcare.</div>';
                }
            });
            el.appendChild(div);
        });
    } catch (e) {
        el.innerHTML = '<div class="urg-intv-loading">Eroare la căutare.</div>';
    }
}

/* ── COLEG AI (intervenții urgente) — implementation shared in colleague.ts ── */
export async function _loadColleagueBrief(logId: number | null, machine: string, problem: string) {
    return colleagueLoadBrief('', {
        context_type: 'log', log_id: logId || 0,
        machine_id: machine || '', problem: problem || '',
    });
}

// The urgent panel form lives in static HTML — wire once at module load.
colleagueWireChat('', () => (_intvLogId
    ? { context_type: 'log', log_id: _intvLogId }
    : null));

export async function _loadAISuggestions(machine: string|null|undefined, problem: string, forceCloud?: boolean) {
    const el = document.getElementById('intv-ai-suggestions');
    if (!el) return;
    const sourceLabel = forceCloud ? 'AI Cloud (Claude)' : 'AI Local';
    el.innerHTML = `<div class="urg-intv-loading">🤖 ${sourceLabel} analizează problema... (poate dura 30-90 secunde)</div>`;

    try {
        const query = 'Mașina: ' + machine + '. Problema: ' + problem +
            '\n\nDă sugestii scurte (max 5 puncte) despre ce ar trebui verificat și ce ar putea cauza această problemă. Răspunde concis în română.';
        const payload: { message: string; machine_id: string | null | undefined; context_module: string; escalate?: boolean } =
            { message: query, machine_id: machine, context_module: 'maintenance' };
        if (forceCloud) payload.escalate = true;
        const res = await api('/api/chat', {
            method: 'POST',
            headers: {'Content-Type':'application/json'},
            body: JSON.stringify(payload)
        });
        const answer = res.reply || res.response || '';
        const source = res.source || '';
        if (!answer) {
            el.innerHTML = '<div class="urg-intv-loading">AI-ul nu a returnat sugestii.</div>' +
                _buildCloudBtn(machine, problem, forceCloud!);
            return;
        }

        const isNoInfo = _aiAnswerIsEmpty(answer);
        const lines = answer.split('\n').filter((l: string) => l.trim());
        let html = '';
        if (source) {
            html += `<div style="font-size:.75rem;color:var(--text-muted);margin-bottom:6px">Sursă: ${escHtml(source === 'local' ? 'AI Local' : 'AI Cloud (Claude)')}</div>`;
        }
        const isNumberedStep = (l: string) => /^\s*\d+[\.\)]\s/.test(l) || /^\*\*\d+/.test(l);
        lines.forEach((l: string|null|undefined) => {
            const escaped = escHtml(l);
            const linkedText = escaped.replace(/\b([A-Z]{2,}\d[\w\-\/]*\d)\b/g,
                '<a href="#" class="urg-ai-part-ref" data-pn="$1" data-mid="' + escHtml(machine) + '" style="color:#fb923c;text-decoration:underline;cursor:pointer;font-weight:600">$1</a>');
            if (isNumberedStep(l!)) {
                const searchTerm = l!.replace(/^\s*\d+[\.\)]\s*/, '').replace(/\*\*/g, '').substring(0, 80);
                html += `<div class="urg-ai-step" style="display:flex;align-items:flex-start;gap:8px">
                    <div style="flex:1">${linkedText}</div>
                    <a href="#" class="urg-ai-manual-link" data-machine="${escHtml(machine)}" data-search="${escHtml(searchTerm)}" style="flex-shrink:0;font-size:.78rem;padding:3px 10px;border-radius:6px;background:rgba(0,180,255,.1);border:1px solid rgba(0,180,255,.3);color:#00b4ff;text-decoration:none;white-space:nowrap;cursor:pointer">📖 Manual</a>
                </div>`;
            } else {
                html += `<div class="urg-ai-step">${linkedText}</div>`;
            }
        });

        if (!forceCloud) {
            html += _buildCloudBtn(machine, problem, false);
        }

        el.innerHTML = html;

        el.querySelectorAll('.urg-btn-ask-cloud').forEach(btn => {
            btn.addEventListener('click', () => _loadAISuggestions(machine, problem, true));
        });
        el.querySelectorAll('.urg-ai-manual-link').forEach(link => {
            link.addEventListener('click', async (e) => {
                e.preventDefault();
                const btn = link as HTMLElement;
                const origText = btn.innerHTML;
                btn.innerHTML = '⏳...';
                const mid = btn.dataset.machine || '';
                const search = btn.dataset.search || '';
                try {
                    const chapters: CapitolRelevant[] = await api('/api/documents/relevant_chapters?machine_id=' +
                        encodeURIComponent(mid) + '&problem=' + encodeURIComponent(search));
                    if (chapters && chapters.length > 0) {
                        window.open(chapters[0].pdf_url, '_blank');
                        btn.innerHTML = origText;
                        return;
                    }
                    const partMatch = search.match(/\b([A-Z]{2,}\d[\w\-\/]*\d)\b/);
                    if (partMatch) {
                        const ch2: CapitolRelevant[] = await api('/api/documents/relevant_chapters?machine_id=' +
                            encodeURIComponent(mid) + '&problem=' + encodeURIComponent(partMatch[1]));
                        if (ch2 && ch2.length > 0) {
                            window.open(ch2[0].pdf_url, '_blank');
                            btn.innerHTML = origText;
                            return;
                        }
                    }
                    const docs = await api('/api/documents/for_machine/' + encodeURIComponent(mid));
                    if (docs && docs.length > 0) {
                        window.open('/api/documents/' + docs[0].id + '/view', '_blank');
                    } else {
                        toast('Nu s-a găsit manual pentru această mașină', 'warning');
                    }
                } catch (_e) { toast('Eroare la căutare manual', 'error'); }
                btn.innerHTML = origText;
            });
        });
        el.querySelectorAll('.urg-ai-part-ref').forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const pn = (link as HTMLElement).dataset.pn || '';
                const mid = (link as HTMLElement).dataset.mid || '';
                const partsNav = document.querySelector('.nav-links a[data-section="parts"]') as HTMLElement;
                if (partsNav) partsNav.click();
                setTimeout(() => {
                    const tabBtn = document.querySelector('[data-tab="parts-tab-catalog"]');
                    if (tabBtn) (tabBtn as HTMLElement).click();
                    setTimeout(() => {
                        const searchEl = document.getElementById('parts-search') as HTMLInputElement;
                        const machineEl = document.getElementById('parts-machine') as HTMLSelectElement;
                        if (machineEl) machineEl.value = mid || '';
                        if (searchEl && pn) searchEl.value = pn;
                        const loadBtn = document.getElementById('parts-btn') as HTMLButtonElement;
                        if (loadBtn) loadBtn.click();
                    }, 150);
                }, 300);
            });
        });

        _loadAIReferencedDocs(machine!, answer, el);
    } catch (e) {
        el.innerHTML = '<div class="urg-intv-loading">Sugestiile AI nu sunt disponibile momentan.</div>' +
            _buildCloudBtn(machine, problem, forceCloud!);
        el.querySelectorAll('.urg-btn-ask-cloud').forEach(btn => {
            btn.addEventListener('click', () => _loadAISuggestions(machine, problem, true));
        });
    }
}

export function _aiAnswerIsEmpty(answer: string | null | undefined) {
    const lower = (answer || '').toLowerCase();
    const noInfoPhrases = [
        'nu am găsit', 'nu am gasit', 'nu există informați', 'nu exista informati',
        'nu am informați', 'nu am informati', 'nu sunt disponibil',
        'documentația disponibilă', 'documentatia disponibila',
        'reformulezi întrebarea', 'reformulezi intrebarea',
        'solicită detalii', 'solicita detalii',
        'ai cloud', 'escaladare',
        'nu am putut formula', 'nu am putut', 'nu pot formula',
        'nu dispun de informati', 'nu am date', 'nu am suficiente'
    ];
    return noInfoPhrases.some(p => lower.includes(p));
}

export function _buildCloudBtn(machine: string | null | undefined, problem: string, alreadyCloud: boolean) {
    if (alreadyCloud) return '';
    return `<div style="margin-top:12px;padding:12px;background:rgba(59,130,246,.08);border-radius:10px;border:1px solid rgba(59,130,246,.2)">
        <div style="font-size:.9rem;color:var(--text-muted);margin-bottom:8px">
            AI Local nu a găsit informații specifice. Poți interoga AI Cloud (Claude) care are capacități avansate de analiză.
        </div>
        <button type="button" class="urg-btn-ask-cloud" style="
            padding:10px 20px;border:none;border-radius:8px;
            background:linear-gradient(135deg,#3b82f6,#8b5cf6);color:#fff;
            font-size:.95rem;font-weight:600;cursor:pointer;
            display:inline-flex;align-items:center;gap:8px;
            transition:transform .15s,box-shadow .15s;
        ">☁️ Întreabă AI Cloud (Claude)</button>
    </div>`;
}

export async function _loadAIReferencedDocs(machine: string, aiAnswer: string, parentEl: HTMLElement) {
    try {
        const chapters: CapitolRelevant[] = await api('/api/documents/relevant_chapters?machine_id=' +
            encodeURIComponent(machine) + '&problem=' + encodeURIComponent(aiAnswer.substring(0, 500)));
        if (!chapters || chapters.length === 0) return;
        const top = chapters.filter((ch) => ch.relevance >= 2).slice(0, 6);
        if (top.length === 0) return;
        let html = '<div class="urg-ai-docs-section"><strong>📖 Manual de referință:</strong>';
        top.forEach((ch) => {
            const pageInfo = ch.start_page != null ? ` (pag. ${ch.start_page + 1}${ch.end_page != null && ch.end_page !== ch.start_page ? '–' + (ch.end_page + 1) : ''})` : '';
            html += `<a href="${ch.pdf_url}" target="_blank" class="urg-doc-link">
                <span class="urg-doc-chapter">${escHtml(ch.chapter_number || '')}</span>
                <span class="urg-doc-title">${escHtml(ch.title)}${pageInfo}</span>
                <span class="urg-doc-open">📄 PDF</span>
            </a>`;
        });
        html += '</div>';
        parentEl.insertAdjacentHTML('beforeend', html);
    } catch (e) {}
}

/* ── CAPTURA MEDIA (cameră + fișiere) ── */
export let _intvMediaFiles: File[] = [];

export function _intvAddFiles(files: FileList) {
    for (const f of files) {
        if (_intvMediaFiles.some(x => x.name === f.name && x.size === f.size)) continue;
        _intvMediaFiles.push(f);
    }
    _intvRenderPreview();
    _intvIncarcaMediaAcum();
}

/** Pozele / filmările pleacă la server imediat ce sunt făcute — nu mai așteaptă „Salvează"
 *  (se vedeau doar pe tableta care le-a făcut, iar „Pauză" le pierdea). */
let _intvMediaServer: MediaLucrare[] = [];
let _intvIncarc = false;
async function _intvIncarcaMediaAcum(): Promise<void> {
    if (!_intvLogId || _intvIncarc) return;
    _intvIncarc = true;
    try {
        while (_intvMediaFiles.length && _intvLogId) {
            const f = _intvMediaFiles[0];
            const fd = new FormData();
            fd.append('file', f);
            const r = await fetch('/api/maintenance_log/' + _intvLogId + '/recording', { method: 'POST', credentials: 'include', body: fd });
            if (!r.ok) throw new Error('HTTP ' + r.status);
            _intvMediaFiles.shift();
        }
        const lista: MediaLucrare[] = await api('/api/maintenance_log/' + _intvLogId + '/media');
        _intvRandeazaMedia(Array.isArray(lista) ? lista : []);
    } catch (_e) {
        toast('Poza nu s-a încărcat încă (rețea sau sesiune expirată) — rămâne aici și se reîncearcă singură.', 'warning');
        _intvRenderPreview();
    } finally {
        _intvIncarc = false;
    }
}

function _intvRandeazaMedia(lista: MediaLucrare[]): void {
    _intvMediaServer = lista || [];
    _intvSync.nrMedia = _intvMediaServer.length;
    _intvRenderPreview();
}

export function _intvRenderPreview() {
    const el = document.getElementById('intv-media-preview');
    if (!el) return;
    el.innerHTML = '';
    _intvMediaServer.forEach((m) => {
        const thumb = document.createElement('div');
        thumb.className = 'urg-media-thumb';
        if (m.media_type === 'video') {
            thumb.innerHTML = `<video src="${escHtml(m.url)}" muted preload="metadata"></video><div class="urg-media-thumb-label">🎥 Salvat</div>`;
        } else {
            thumb.innerHTML = `<img src="${escHtml(m.url)}" loading="lazy" alt=""><div class="urg-media-thumb-label">📷 Salvat</div>`;
        }
        el.appendChild(thumb);
    });
    _intvMediaFiles.forEach((f, i) => {
        const thumb = document.createElement('div');
        thumb.className = 'urg-media-thumb';
        const isVideo = f.type.startsWith('video/');
        const url = URL.createObjectURL(f);
        if (isVideo) {
            thumb.innerHTML = `<video src="${url}" muted></video><div class="urg-media-thumb-label">🎥 Se încarcă…</div>`;
        } else {
            thumb.innerHTML = `<img src="${url}"><div class="urg-media-thumb-label">📷 Se încarcă…</div>`;
        }
        const removeBtn = document.createElement('button');
        removeBtn.className = 'urg-media-thumb-remove';
        removeBtn.textContent = '✕';
        removeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            _intvMediaFiles.splice(i, 1);
            _intvRenderPreview();
        });
        thumb.appendChild(removeBtn);
        el.appendChild(thumb);
    });
}

document.getElementById('intv-btn-photo')?.addEventListener('click', () => {
    document.getElementById('intv-capture-photo')?.click();
});
document.getElementById('intv-btn-video')?.addEventListener('click', () => {
    document.getElementById('intv-capture-video')?.click();
});
document.getElementById('intv-btn-file')?.addEventListener('click', () => {
    document.getElementById('intv-media-upload')?.click();
});

document.getElementById('intv-capture-photo')?.addEventListener('change', (e) => {
    if ((e.target as HTMLInputElement).files!.length) _intvAddFiles((e.target as HTMLInputElement).files!);
    (e.target as HTMLInputElement).value = '';
});
document.getElementById('intv-capture-video')?.addEventListener('change', (e) => {
    if ((e.target as HTMLInputElement).files!.length) _intvAddFiles((e.target as HTMLInputElement).files!);
    (e.target as HTMLInputElement).value = '';
});
document.getElementById('intv-media-upload')?.addEventListener('change', (e) => {
    if ((e.target as HTMLInputElement).files!.length) _intvAddFiles((e.target as HTMLInputElement).files!);
    (e.target as HTMLInputElement).value = '';
});

document.getElementById('intv-day-pause-btn')?.addEventListener('click', async () => {
    if (!_intvLogId) { toast('Nu există intervenție activă!', 'error'); return; }
    if (!await customConfirm('Pui intervenția în pauză?\nCronometrul se oprește. Ce ai scris și pozele sunt salvate — o redeschizi de pe orice dispozitiv exact de unde ai rămas.', WORK_LIFECYCLE_LABELS.pause)) return;
    // ce s-a scris și pozele pleacă la server ÎNAINTE de pauză (înainte se pierdeau)
    if (!await _intvSalveazaCiorna()) { toast('Nu pot salva textul acum — verifică rețeaua și reîncearcă.', 'error'); return; }
    await _intvIncarcaMediaAcum();
    clearInterval(_intvTimerInterval ?? undefined);
    const duration = _intvGetDurationMinutes();
    try {
        await workPause('maintenance_log', _intvLogId, { duration_minutes: duration });
        _intvResetSincronizare();
        toast('Intervenție în pauză — poți continua mâine din lista de redeschidere.', 'success');
        _intvLogId = null;
        _showActiveInterventionBanner();
        urgShowPhase('urg-phase-1');
        await _renderUrgentReopenList();
    } catch (e: unknown) {
        toast('Eroare: ' + ((e as Error).message || String(e)), 'error');
    }
});

/* ── FINALIZARE INTERVENȚIE ── */
document.getElementById('intv-save-btn')?.addEventListener('click', async () => {
    if (!_intvLogId) { toast('Nu există intervenție activă!', 'error'); return; }

    const elActiuni = document.getElementById('intv-actions-done') as HTMLInputElement | null;
    const elConstatari = document.getElementById('intv-constatari') as HTMLTextAreaElement | null;
    const elNote = document.getElementById('jrn-notes') as HTMLInputElement | null;
    const elCauza = document.getElementById('intv-cause') as HTMLInputElement | null;
    let actionsDone = (elActiuni?.value || '').trim();
    let constatari = (elConstatari?.value || '').trim();
    let notesVal = (elNote?.value || '').trim();
    if (!actionsDone && !notesVal && !constatari) {
        toast('Completează minim „Ce s-a constatat", „Ce s-a făcut" sau „Observații suplimentare"!', 'warning');
        document.getElementById('intv-actions-done')?.focus();
        document.getElementById('intv-actions-done')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
    }

    // Corectura scrierii (1 nov 2026, Dan): constatările, acțiunile, cauza și observațiile se văd corectate și se
    // aprobă într-un singur ecran; câmpurile primesc textul aprobat, ca și ciorna să rămână la fel cu ce se salvează
    let cauza = (elCauza?.value || '').trim();
    [constatari, actionsDone, cauza, notesVal] = await confirmaScrierea([
        { eticheta: 'Ce s-a constatat', text: constatari }, { eticheta: 'Ce s-a făcut', text: actionsDone },
        { eticheta: 'Cauza', text: cauza }, { eticheta: 'Observații suplimentare', text: notesVal }],
        'interventie-urgenta');
    if (elConstatari) elConstatari.value = constatari;
    if (elActiuni) elActiuni.value = actionsDone;
    if (elCauza) elCauza.value = cauza;
    if (elNote) elNote.value = notesVal;

    await _intvSalveazaCiorna();
    const duration = _intvGetDurationMinutes();
    clearInterval(_intvTimerInterval ?? undefined);
    const now = new Date().toISOString();

    const data = {
        status: 'completed',
        duration_minutes: duration,
        completed_time: now,
        involved_persons: (document.getElementById('intv-persons') as HTMLInputElement)?.value || '',
        parts_used: (document.getElementById('jrn-parts') as HTMLInputElement)?.value || '',
        cause_found: cauza,
        notes: notesVal,
        action: document.getElementById('intv-rpt-problem')?.textContent +
            _intvSectiune('Constatări', constatari) + _intvSectiune('Acțiuni', actionsDone),
    };

    let _validatLaFinal = false;
    try {
        const _rezFinal = await api('/api/maintenance_log/' + _intvLogId, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        // din cont de inginer/master, serverul o validează pe numele celui logat (Dan, 30 sep 2026)
        _validatLaFinal = !!(_rezFinal && _rezFinal.validat_de);

        if (_intvMediaFiles.length > 0) {
            for (const file of _intvMediaFiles) {
                const fd = new FormData();
                fd.append('file', file);
                try {
                    await fetch('/api/maintenance_log/' + _intvLogId + '/recording', {
                        method: 'POST', credentials: 'include', body: fd
                    });
                } catch (e) {}
            }
        }

        // Coleg AI: offer to save the intervention as a lesson learned.
        // Fire-and-forget — the modal must never block the finish/reset flow.
        colleagueOfferLesson({
            // 9 sep 2026: `machine_id` lipsea DOAR aici, din cele cinci locuri
            // care oferă lecția. Rezumatul se genera, dar salvarea pica pe 400.
            context_type: 'log', log_id: _intvLogId,
            machine_id: ($('#jrn-machine') as HTMLSelectElement | null)?.value || '',
            actions: (constatari ? 'Constatări: ' + constatari + '\n\n' : '') + actionsDone,
            cause: data.cause_found,
            parts: data.parts_used,
            notes: notesVal,
        }).catch(() => {});

        let durataFinala = duration;
        try { durataFinala = (await api('/api/maintenance_log/' + _intvLogId)).duration_minutes || duration; } catch (_e) { /* */ }
        toast('✅ Intervenția a fost finalizată și salvată! Durată: ' + durataFinala + ' minute'
            + (_validatLaFinal ? ' — validată pe numele tău' : ' — intră la validare'));
        _intvResetSincronizare();
        _intvMediaServer = [];
        _intvLogId = null;
        _showActiveInterventionBanner();
        _intvMediaFiles = [];
        urgShowPhase('urg-phase-1');
        (document.getElementById('intv-actions-done') as HTMLInputElement).value = '';
        (document.getElementById('intv-cause') as HTMLInputElement).value = '';
        (document.getElementById('jrn-parts') as HTMLInputElement).value = '';
        (document.getElementById('jrn-notes') as HTMLInputElement).value = '';
        (document.getElementById('intv-persons') as HTMLInputElement).value = '';
        const preview = document.getElementById('intv-media-preview');
        if (preview) preview.innerHTML = '';
    } catch (e) {
        toast('Eroare la salvare: ' + (e as Error).message, 'error');
    }
});

document.getElementById('intv-cancel-btn')?.addEventListener('click', async () => {
    if (!await customConfirm('Sigur anulezi intervenția?\nDatele nu vor fi salvate.', '⚠ Anulare intervenție')) return;
    clearInterval(_intvTimerInterval ?? undefined);
    if (_intvLogId) {
        try {
            await api('/api/maintenance_log/' + _intvLogId, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: 'cancelled' })
            });
        } catch (e) {}
    }
    _intvResetSincronizare();
    _intvLogId = null;
    _showActiveInterventionBanner();
    urgShowPhase('urg-phase-1');
});

document.getElementById('urgent-history-btn')?.addEventListener('click', () => {
    urgShowPhase('urg-phase-history');
    loadJournalLog();
});
document.getElementById('urg-history-back')?.addEventListener('click', () => urgShowPhase('urg-phase-1'));
